import React, { useState, useEffect, createContext, useContext } from 'react';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import FilterBar from './FilterBar';
import Footer from './Footer';

const LayoutContext = createContext(null);

export function useLayout() {
  return useContext(LayoutContext);
}

export default function Layout({ children }) {
  // Desktop: collapse the sidebar to an icon rail.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Mobile (<lg): the sidebar becomes an off-canvas drawer toggled by the
  // hamburger in the TopBar. Closed by default so content is full-width.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [timeRange, setTimeRange] = useState('24h');
  const [granularity, setGranularity] = useState('1h');

  // Force light mode / SCE branding — the dark theme has been removed.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  // Close the mobile drawer whenever the viewport grows to desktop, so we
  // don't leave a stale overlay when rotating / resizing.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => { if (e.matches) setMobileSidebarOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return (
    <LayoutContext.Provider value={{ timeRange, setTimeRange, granularity, setGranularity }}>
      <div className="flex flex-col h-screen overflow-hidden bg-white text-sce-dark">
        <div className="flex flex-1 overflow-hidden">
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
            mobileOpen={mobileSidebarOpen}
            onMobileClose={() => setMobileSidebarOpen(false)}
          />
          <div className="flex flex-col flex-1 overflow-hidden">
            <TopBar
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
              granularity={granularity}
              onGranularityChange={setGranularity}
              onMobileMenu={() => setMobileSidebarOpen(true)}
            />
            <FilterBar />
            <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-white">
              {children}
            </main>
          </div>
        </div>
        {/* Footer spans the full screen width, below both sidebar and content. */}
        <Footer />
      </div>
    </LayoutContext.Provider>
  );
}
